import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { isNative } from "../api";
import { setSetting } from "../db/actions";
import { AppearanceSettings } from "./Appearance";
import { AppUpdateSettings } from "./AppUpdates";
import { DevicesDialog } from "./Devices";
import { FeedbackDialog, FeedbackList } from "./Feedback";
import { ExportDialog } from "./ExportLazy";
import { useLayout } from "./layout";
import { CustomizeNav } from "./navConfig";
import { useNesting } from "./notebookTree";
import { usePrefs } from "./prefs";
import { Avatar, setDisplayName, useProfile } from "./profile";

/*
 * Settings (account menu > Settings, sidebar More > Settings): one page for the choices that used to be
 * spread over menus. Appearance first (the colour theme sticks across restarts and devices).
 */

function Switch({ label, hint, on, onChange }: { label: string; hint?: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button className="settings-row clickable" role="switch" aria-checked={on} onClick={() => onChange(!on)}>
      <div>
        <div className="settings-label">{label}</div>
        {hint && <div className="settings-hint">{hint}</div>}
      </div>
      <span className={`switch${on ? " on" : ""}`}>
        <span />
      </span>
    </button>
  );
}

function Link({ label, hint, onClick }: { label: string; hint?: string; onClick: () => void }) {
  return (
    <button className="settings-row clickable" onClick={onClick}>
      <div>
        <div className="settings-label">{label}</div>
        {hint && <div className="settings-hint">{hint}</div>}
      </div>
      <ChevronRight size={16} className="muted" />
    </button>
  );
}

export function SettingsPage() {
  const prefs = usePrefs();
  const layout = useLayout();
  const nesting = useNesting();
  const [dialog, setDialog] = useState<null | "nav" | "devices" | "export" | "feedback">(null);
  const profile = useProfile();
  const savedName = profile.name;
  const [name, setName] = useState<string | null>(null);

  return (
    <div className="page settings-page">
      <div className="page-header">
        <h1>Settings</h1>
      </div>

      <section className="settings-section">
        <h2>Appearance</h2>
        <AppearanceSettings />
      </section>

      <section className="settings-section">
        <h2>Navigation</h2>
        <Link
          label={layout === "wide" ? "Customize sidebar" : "Customize navigation"}
          hint="Show, hide and reorder items; Today's note and its notebook."
          onClick={() => setDialog("nav")}
        />
        <Switch label="Allow stacks inside stacks" hint="Off keeps Evernote's two levels (stacks, then notebooks)." on={nesting} onChange={(v) => void setSetting("notebooks.nesting", v)} />
      </section>

      <section className="settings-section">
        <h2>Editor</h2>
        <Switch
          label="Collapsible sections"
          hint="Headings get an arrow that folds the text under them. On this device."
          on={prefs.collapsibleSections}
          onChange={(v) => prefs.set({ collapsibleSections: v })}
        />
      </section>

      <section className="settings-section">
        <h2>Account</h2>
        <div className="settings-row settings-account">
          <Avatar profile={profile} size={44} />
          <div>
            <div className="settings-label">{profile.name || "Engram"}</div>
            <div className="settings-hint">
              {profile.email ? `${profile.email} · name and photo from your Google account` : "Sign in with Google to bring your name and photo."}
            </div>
          </div>
        </div>
        <div className="settings-row">
          <div>
            <div className="settings-label">Display name</div>
            <div className="settings-hint">Shown in the Home greeting and on the avatar.</div>
          </div>
          <input
            className="input settings-input"
            value={name ?? savedName}
            placeholder="Your name"
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              // Once edited here, a Google sign-in keeps this name.
              if (name !== null && name.trim() !== savedName) void setDisplayName(name);
              setName(null);
            }}
            onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
          />
        </div>
        <Link label="Devices & tokens" hint="Signed-in devices, the backup token and Claude tokens." onClick={() => setDialog("devices")} />
        <Link label="Export all notes" hint="Markdown, HTML or Evernote ENEX." onClick={() => setDialog("export")} />
      </section>

      <section className="settings-section">
        <h2>Updates</h2>
        <AppUpdateSettings />
        <Switch
          label="Ask before updating"
          hint={`Off: a new version is used ${isNative ? "the next time Engram starts" : "while you're on another tab or window"}, once your changes have synced. On: you choose when, with Restart. On this device.`}
          on={prefs.askBeforeUpdating}
          onChange={(v) => prefs.set({ askBeforeUpdating: v })}
        />
      </section>

      <section className="settings-section">
        <h2>Feedback</h2>
        <Link label="Send feedback" hint="A bug, an idea, anything. Claude reads it at the start of the next chat." onClick={() => setDialog("feedback")} />
        <FeedbackList />
      </section>

      {dialog === "feedback" && <FeedbackDialog onClose={() => setDialog(null)} />}
      {dialog === "nav" && <CustomizeNav kind={layout === "wide" ? "sidebar" : "rail"} onClose={() => setDialog(null)} />}
      {dialog === "devices" && <DevicesDialog onClose={() => setDialog(null)} />}
      {dialog === "export" && <ExportDialog scope={{ kind: "all" }} onClose={() => setDialog(null)} />}
    </div>
  );
}
